import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PTabsBar } from "@porsche-design-system/components-react"
import { spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { HeaderView } from "@slfinrtl/ui-bff-routes"
import {
	generateEnergyEfficiencyLabelRoute,
	getMonroneyLabelByInventoryIdRoute,
	getVehicleDetailsHeaderRoute,
} from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useMemo, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { Link, Outlet, useLocation, useParams } from "react-router"
import { openPdf } from "src/common/pdf/openPdf"
import { useDealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import { AppTitle, BackNavigation, BackNavigationWrapper, ContentWrapper, Main, StageList } from "src/components"
import { getVehicleDetailsAnalyticsPage, trackPageView } from "src/components/adobeAnalytics"
import { useEditStageContextForStage } from "src/context/editStageContext/useEditStageContext"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff } from "src/hooks"
import type { VehicleDetailsStage } from "./common/VehicleDetailsStage"
import { useVehicleDetailsContext } from "./context/VehicleDetailsContext"
import { VehicleDetailsContextProvider } from "./context/VehicleDetailsContextProvider"
import { HeaderContainer } from "./headerContainer/HeaderContainer"
import { PublishingRuleViolationsContainer } from "./publishingRuleViolationsContainer/PublishingRuleViolationsContainer"

const messages = defineMessages({
	sectionOverview: {
		defaultMessage: "Overview",
		id: "vehicleDetails.header.overview.title",
	},
	sectionSalesActivities: {
		defaultMessage: "Sales Activity",
		id: "vehicleDetails.header.salesActivity.title",
	},
	sectionVehicleHistory: {
		defaultMessage: "Vehicle History",
		id: "vehicleDetails.header.vehicleHistory.title",
	},
})

interface RouteProps {
	vehicleKey: string
}

export const VehicleDetailsContainer = () => {
	const { vehicleKey } = useParams<keyof RouteProps>() as RouteProps
	const { dealershipInfo } = useDealerContext()

	return (
		<Main>
			<AppTitle locationCode={dealershipInfo.locationCode} />
			<BackNavigationWrapper>
				<BackNavigation />
			</BackNavigationWrapper>

			{vehicleKey && (
				<VehicleDetailsContextProvider inventoryId={vehicleKey}>
					<VehicleDetailsContainerInner />
				</VehicleDetailsContextProvider>
			)}
		</Main>
	)
}

const VehicleDetailsContainerInner = withErrorHandler(() => {
	const [header, setHeader] = useState<HeaderView | null>()

	const { dealershipInfo } = useDealerContext()
	const { formatMessage } = useIntl()
	const { pathname } = useLocation()
	const { inventoryId, subscribe } = useVehicleDetailsContext()
	const routes = useDealerAppRoutes()

	// VehicleDetailsContainer is registered as the parent route under all 3 entry points
	// (inventoryList, the legacy deliveredVehiclesList and fulfillmentDeliveredVehiclesList.usedVehicles)
	const salesPath = routes?.inventoryList?.vehicleDetails?.sales?.path
	const buildsheetPath = routes?.inventoryList?.vehicleDetails?.buildsheet?.path
	const vehicleHistoryPath = routes?.inventoryList?.vehicleDetails?.vehicleHistory.path ?? "vehicle-history"

	const tabsBarLinks = useMemo<{ label: string; to: string }[]>(
		() =>
			[
				{
					label: formatMessage(messages.sectionOverview),
					to: ".",
				},
				salesPath && {
					label: formatMessage(messages.sectionSalesActivities),
					to: salesPath,
				},
				buildsheetPath && {
					label: "Build Sheet",
					to: buildsheetPath,
				},
				{
					label: formatMessage(messages.sectionVehicleHistory),
					to: vehicleHistoryPath,
				},
			].filter((item) => !!item),
		[formatMessage, salesPath, buildsheetPath, vehicleHistoryPath],
	)

	const activeTabIndex = useMemo<number>(() => {
		const index = tabsBarLinks.findIndex((link) => link.to !== "." && pathname.endsWith(`/${link.to}`))
		return index === -1 ? 0 : index
	}, [pathname, tabsBarLinks])

	// cancel edit mode when switching tabs
	const { setEditStage } = useEditStageContextForStage<VehicleDetailsStage>("inventoryIntake") // stageId does not matter since we just need the setter
	const onTabsBarLinkClick = useCallback(() => setEditStage(undefined), [setEditStage])

	useEffect(() => {
		trackPageView(getVehicleDetailsAnalyticsPage(pathname, "overview"))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const fetchHeader = useBff(
		getVehicleDetailsHeaderRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)
	useEffect(() => {
		fetchHeader()
			.then(setHeader)
			.catch(() => setHeader(null))
	}, [fetchHeader, setHeader])

	useEffect(() => {
		subscribe("saleCancelled", () => {
			setTimeout(
				() =>
					fetchHeader()
						.then(setHeader)
						.catch(() => setHeader(null)),
				1000,
			)
		})
	}, [fetchHeader, subscribe])

	const getEELLink = useBff(
		generateEnergyEfficiencyLabelRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	const getMonroneyLabelUrl = useBff(
		getMonroneyLabelByInventoryIdRoute,
		{ body: undefined, pathParams: { inventoryId }, queryParams: {} },
		"global",
	)

	const onMonroneyLabelClick = useCallback(async () => {
		const monroneyLabelView = await getMonroneyLabelUrl()
		window.open(monroneyLabelView.downloadUrl, "_blank")
	}, [getMonroneyLabelUrl])

	const onEELClick = useCallback(() => getEELLink().then((response) => openPdf(response.document)), [getEELLink])

	return (
		<>
			<AppTitle title={header?.title} locationCode={dealershipInfo.locationCode} />
			<ContentWrapper>
				<ErrorNotificationInline />
			</ContentWrapper>

			{header !== null && (
				<HeaderContainer onEELClick={onEELClick} onMonroneyLabelClick={onMonroneyLabelClick} view={header} />
			)}

			<StyledContentWrapper>
				<PublishingRuleViolationsContainer />
				{tabsBarLinks.length > 0 && (
					<PTabsBar background="canvas" activeTabIndex={activeTabIndex}>
						{tabsBarLinks.map((item) => (
							<Link key={item.to} to={item.to} replace onClick={onTabsBarLinkClick}>
								{item.label}
							</Link>
						))}
					</PTabsBar>
				)}
			</StyledContentWrapper>

			<StageList>
				<Outlet />
			</StageList>
		</>
	)
})

const StyledContentWrapper = styled(ContentWrapper)`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticMd};
`
